import React, {useState} from "react";
import useAppContext from "../../hooks/useAppContext";
import {Button, DatePicker, Select} from 'antd';
import {ConversionsOverTime} from "../../components/charts/ConversionsOverTime";
import { TemplatesAnalyzer } from "../../components/charts/TemplatesAnalyzer";
import {CheckoutsRecoverableVsRecovered} from "../../components/charts/CheckoutsRecoverableVsRecovered";
import "./dashboard.css";
import Page from "../../components/page/Page";
import DashboardBox from "../../components/dashboardBox/DashboardBox";
import millify from "millify";
import SkeletonItem from "../../components/skeleton/SkeletonItem";
import DashboardPreview from "../../components/dashboardPreview/DashboardPreview";
import BoostRocket from "../../components/BoostRocket/BoostRocket";
import CampaignAveragesChart from "../../components/charts/CampaignAveragesChart";
import DashboardCampaignPreview from "../../components/dashboardPreview/DashboardCampaignPreview";
import {useNavigate} from "react-router-dom";

enum DASHBOARD_TYPES {
    ALL = 'all',
    RECOVERY = 'recovery',
    CAMPAIGN = 'campaign',
}

const Dashboard = () => {
    const { days, actions: { setDays }, data: { shop, dashboardBoxes, templatesAnalyzer, checkoutRecoverableVsRecovered, campaignBoxes, campaignsAvg } } = useAppContext();
    const recoveredRevenue = dashboardBoxes.isLoading ? 0 : dashboardBoxes.data.reduce((acc: number, record: any) => acc + parseFloat(record.totalRevenue), 0);
    const recoverableRevenue = dashboardBoxes.isLoading ? 0 : dashboardBoxes.data.reduce((acc: number, record: any) => acc + parseFloat(record.totalPotentialRevenue), 0);
    const recoveredOrders = dashboardBoxes.isLoading ? 0 : dashboardBoxes.data.reduce((acc: number, record: any) => acc + parseInt(record.totalIsRecoveredOrder), 0);
    const recoverableOrders = dashboardBoxes.isLoading ? 0 : dashboardBoxes.data.reduce((acc: number, record: any) => acc + parseInt(record.totalIsRecoverableOrder), 0);
    const lostOrders = dashboardBoxes.isLoading ? 0 : dashboardBoxes.data.reduce((acc: number, record: any) => acc + parseInt(record.totalLostOrder), 0);
    const totalRecoveredRevenue = dashboardBoxes.isLoading ? 0 : dashboardBoxes.data.reduce((acc: number, record: any) => acc + parseFloat(record.totalRecoveredRevenue), 0);
    const totalPayments = dashboardBoxes.isLoading ? 0 : dashboardBoxes.data.reduce((acc: number, record: any) => acc + parseFloat(record.totalPayments), 0);
    const totalCreditPayments = dashboardBoxes.isLoading ? 0 : dashboardBoxes.data.reduce((acc: number, record: any) => acc + parseFloat(record.totalCreditPayments), 0);
    const totalFeeCost = dashboardBoxes.isLoading ? 0 : dashboardBoxes.data.reduce((acc: number, record: any) => acc + parseFloat(record.dailyFeeCost), 0);
    const totalRecoveryRoa = (Math.round(totalRecoveredRevenue / (totalPayments + totalFeeCost + totalCreditPayments) * 10) / 10) || 0;
    const recoveryRate = recoverableOrders + recoveredOrders + lostOrders > 0 ? Math.round(recoveredOrders/(recoverableOrders + recoveredOrders + lostOrders)*100) : 0;
    const totalCampaignMessagesSent = campaignBoxes.isLoading ? 0 : campaignBoxes.data.reduce((acc: number, record: any) => acc + parseInt(record.messages), 0);
    const totalCampaignClicks = campaignBoxes.isLoading ? 0 : campaignBoxes.data.reduce((acc: number, record: any) => acc + parseInt(record.clicks), 0);
    const totalCampaignConversions = campaignBoxes.isLoading ? 0 : campaignBoxes.data.reduce((acc: number, record: any) => acc + parseInt(record.orders), 0);
    const totalCampaignRevenue = campaignBoxes.isLoading ? 0 : campaignBoxes.data.reduce((acc: number, record: any) => acc + parseFloat(record.revenue), 0);
    const totalCampaignCost = campaignBoxes.isLoading ? 0 : campaignBoxes.data.reduce((acc: number, record: any) => acc + parseFloat(record.cost), 0);
    const totalCampaigns = campaignBoxes.isLoading ? 0 : campaignBoxes.data.reduce((acc: number, record: any) => acc + parseInt(record.campaigns), 0);
    const totalAOV = Math.round(totalCampaignRevenue / totalCampaignConversions) || 0;
    const totalCTR = (Math.round(totalCampaignClicks / totalCampaignMessagesSent * 100)) || 0;
    const totalCampaignRoa = (Math.round(totalCampaignRevenue / totalCampaignCost * 10) / 10) || 0;

    const [customDateFrom, setCustomDateFrom] = useState<Date | null>(null);
    const [customDateTo, setCustomDateTo] = useState<Date | null>(null);
    const [view, setView] = useState(DASHBOARD_TYPES.ALL);
    const navigate = useNavigate();

    return (
        <Page className="dashboard-container">
            <div className="filters">
                <div className="days-picker-container">
                    <Select
                        defaultValue={view}
                        style={{ width: 200 }}
                        onChange={(newVal: DASHBOARD_TYPES) => setView(newVal)}
                        options={[
                            { value: DASHBOARD_TYPES.ALL, label: 'View All' },
                            { value: DASHBOARD_TYPES.RECOVERY, label: 'Recoveries View' },
                            { value: DASHBOARD_TYPES.CAMPAIGN, label: 'Campaigns View' },
                        ]}
                    />
                    <Select
                        defaultValue={days.toString()}
                        style={{ width: 120 }}
                        onChange={(newVal: string) => setDays(parseInt(newVal))}
                        options={[
                            { value: "1", label: 'Day' },
                            { value: "7", label: 'Week' },
                            { value: "30", label: 'Month' },
                            { value: "60", label: '2 Months' },
                            { value: "90", label: '3 Months' },
                            { value: "365", label: 'Year' },
                            // { value: "0", label: 'Custom'},
                        ]}
                    />
                    {days === 0 ? <div className="custom-dates">
                        <div>
                            <label>From: </label>
                            <DatePicker className="date-field" onChange={setCustomDateFrom} value={customDateFrom} />
                        </div>
                        <div>
                            <label>To: </label>
                            <DatePicker className="date-field" onChange={setCustomDateTo} value={customDateTo} />
                        </div>
                    </div> : null}
                </div>
            </div>

            <div className="dashboard-layout">
                {[DASHBOARD_TYPES.ALL, DASHBOARD_TYPES.RECOVERY].includes(view) ?
                    <>
                        <div className="dashboard-item responsive-height grid-4">
                            {dashboardBoxes.isLoading ? <SkeletonItem height={200} gridSpan={4} /> : <DashboardBox
                                title="Recovery ROAS"
                                total={millify(totalRecoveryRoa)}
                                data={dashboardBoxes.data}
                                dataKey='roa'
                            />}
                        </div>
                        <div className="dashboard-item responsive-height grid-4">
                            {dashboardBoxes.isLoading ? <SkeletonItem height={200} gridSpan={4} /> : <DashboardBox
                                title="Recovered Orders"
                                total={millify(recoveredOrders)}
                                data={dashboardBoxes.data}
                                dataKey='totalIsRecoveredOrder'
                            />}
                        </div>
                        <div className="dashboard-item responsive-height grid-4">
                            {dashboardBoxes.isLoading ? <SkeletonItem height={200} gridSpan={4} /> : <DashboardBox
                                title="Recoverable Orders"
                                total={millify(recoverableOrders)}
                                data={dashboardBoxes.data}
                                dataKey='totalIsRecoverableOrder'
                            />}
                        </div>
                        <div className="dashboard-item responsive-height grid-4">
                            {dashboardBoxes.isLoading ? <SkeletonItem height={200} gridSpan={4} /> : <DashboardBox
                                title="Recovery Rate"
                                total={`${recoveryRate}%`}
                                data={dashboardBoxes.data}
                                dataKey='recoveryRate'
                            />}
                        </div>
                        <div className="dashboard-item responsive-height grid-4">
                            {dashboardBoxes.isLoading ? <SkeletonItem height={200} gridSpan={4} /> : <DashboardBox
                                title="Recovered Revenue"
                                total={`$${millify(recoveredRevenue)}`}
                                data={dashboardBoxes.data}
                                dataKey='totalRevenue'
                            />}
                        </div>
                        <div className="dashboard-item responsive-height grid-4">
                            {dashboardBoxes.isLoading ? <SkeletonItem height={200} gridSpan={4} /> : <DashboardBox
                                title="Recoverable Revenue"
                                total={`$${millify(recoverableRevenue)}`}
                                data={dashboardBoxes.data}
                                dataKey='totalPotentialRevenue'
                            />}
                        </div>
                        <div className="separator-line grid-12" />
                        <div className="dashboard-item no-padding grid-7"><DashboardPreview title="Recent Abandoned Checkouts" type="checkouts" route="checkouts" /></div>
                        {/*<div className="dashboard-item no-padding grid-5"><RecoveryFunnelBox fromDate={customDateFrom || undefined} toDate={customDateTo || undefined} days={days} /></div>*/}
                        {/*<div className="dashboard-item no-padding grid-6"><DashboardPreview title="Recent Messages Sent" type="messages" route="messages" /></div>*/}
                        {/*<div className="dashboard-item grid-5">{conversionsOverTime.isLoading ? <SkeletonItem height={200} gridSpan={6} /> : <ConversionsOverTime />}</div>*/}
                        <div className="dashboard-item abandoned-vs-recovered grid-5">{checkoutRecoverableVsRecovered.isLoading ? <SkeletonItem height={200} gridSpan={6} /> : <CheckoutsRecoverableVsRecovered />}</div>
                        {/*<div className="dashboard-item grid-4"><TotalRevenueUsd /></div>*/}
                        {/*<div className="dashboard-item grid-8"></div>*/}
                        {/*<div className="separator-line grid-12" />*/}
                        {/*<div className="dashboard-item grid-12">{templates.isLoading ? <SkeletonItem height={300} gridSpan={6} /> : <CheckoutMessageOrderFlow />}</div>*/}

                        {/*<div className="separator-line grid-12" />*/}

                        {/*<div className="dashboard-item grid-4">*/}
                        {/*    {dashboardBoxes.isLoading ? <SkeletonItem height={200} gridSpan={4} /> : <DashboardBox*/}
                        {/*        title="Recovery Rate"*/}
                        {/*        total={`${recoveryRate}%`}*/}
                        {/*        data={dashboardBoxes.data}*/}
                        {/*        dataKey='recoveryRate'*/}
                        {/*    />}*/}
                        {/*</div>*/}
                        {/*<div className="dashboard-item grid-4">*/}
                        {/*    {dashboardBoxes.isLoading ? <SkeletonItem height={200} gridSpan={4} /> : <DashboardBox*/}
                        {/*        title="Recovered Revenue"*/}
                        {/*        total={`$${millify(recoveredRevenue)}`}*/}
                        {/*        data={dashboardBoxes.data}*/}
                        {/*        dataKey='totalRevenue'*/}
                        {/*    />}*/}
                        {/*</div>*/}
                        {/*<div className="dashboard-item grid-4">*/}
                        {/*    {dashboardBoxes.isLoading ? <SkeletonItem height={200} gridSpan={4} /> : <DashboardBox*/}
                        {/*        title="Recoverable Revenue"*/}
                        {/*        total={`$${millify(recoverableRevenue)}`}*/}
                        {/*        data={dashboardBoxes.data}*/}
                        {/*        dataKey='totalPotentialRevenue'*/}
                        {/*    />}*/}
                        {/*</div>*/}

                        <div className="separator-line grid-12" />
                        <div className="dashboard-item grid-4">{templatesAnalyzer.isLoading ? <SkeletonItem height={200} gridSpan={4} /> : <TemplatesAnalyzer type="sent" />}</div>
                        <div className="dashboard-item grid-4">{templatesAnalyzer.isLoading ? <SkeletonItem height={200} gridSpan={4} /> : <TemplatesAnalyzer type="read" />}</div>
                        <div className="dashboard-item grid-4">{templatesAnalyzer.isLoading ? <SkeletonItem height={200} gridSpan={4} /> : <TemplatesAnalyzer type="clicked" />}</div>
                        {/*<div className="dashboard-item grid-4">{templatesAnalyzer.isLoading ? <SkeletonItem height={200} gridSpan={4} /> : <TemplatesAnalyzer type="deliveryRate" />}</div>*/}

                    </>
                : null }

                {[DASHBOARD_TYPES.ALL].includes(view) && [24,41,28,50,67,72].includes(shop.data?.id) ?
                    <div className="separator-line grid-12" />
                : null}


                {[DASHBOARD_TYPES.ALL, DASHBOARD_TYPES.CAMPAIGN].includes(view) && [24,41,28,50,67,72].includes(shop.data?.id) ? <>

                    <div className="grid-3 dashboard-vertical-items campaign-booster">
                        <div>
                            <div className="dashboard-item big-text-container" onClick={() => {
                                navigate('/campaigns');
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}>
                                <div className="big-number">{totalCampaigns || 0}</div>
                                <div className="big-number-text">Campaigns</div>
                            </div>
                        </div>
                        <div>
                            <div className="dashboard-item campaign-promotion-container">
                                <BoostRocket height={82} width={82} />
                                {/*<img src={BoostSalesImage} />*/}
                                <div>Boost sales now</div>
                                <Button size="large" type="primary" onClick={() => {
                                    navigate('/campaigns', { state: { autoOpenNewCampaignModal: true }});
                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                }}>Create a Campaign</Button>
                            </div>
                        </div>
                    </div>

                    <div className="grid-9 dashboard-item no-padding">
                        <DashboardCampaignPreview title="Recent Campaigns" route="campaigns" onNewCampaignClick={() => {
                            navigate('/campaigns', { state: { autoOpenNewCampaignModal: true }});
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                        }} />
                    </div>


                    <div className="dashboard-item responsive-height grid-4">
                        {campaignBoxes.isLoading ? <SkeletonItem height={200} gridSpan={4} /> : <DashboardBox
                            title="Campaigns ROAS"
                            total={`${millify(totalCampaignRoa)}`}
                            data={campaignBoxes.data}
                            dataKey='roa'
                        />}
                    </div>
                    <div className="dashboard-item responsive-height grid-4">
                        {campaignBoxes.isLoading ? <SkeletonItem height={200} gridSpan={4} /> : <DashboardBox
                            title="Campaigns AOV"
                            total={`$${millify(totalAOV)}`}
                            data={campaignBoxes.data}
                            dataKey='aov'
                        />}
                    </div>
                    <div className="dashboard-item responsive-height grid-4">
                        {campaignBoxes.isLoading ? <SkeletonItem height={200} gridSpan={4} /> : <DashboardBox
                            title="Campaigns CTR"
                            total={`${millify(totalCTR)}%`}
                            data={campaignBoxes.data}
                            dataKey='ctr'
                        />}
                    </div>
                    <div className="dashboard-item responsive-height grid-4">
                        {campaignBoxes.isLoading ? <SkeletonItem height={200} gridSpan={4} /> : <DashboardBox
                            title="Campaigns Messages Delivered"
                            total={millify(totalCampaignMessagesSent)}
                            data={campaignBoxes.data}
                            dataKey='messages'
                        />}
                    </div>
                    <div className="dashboard-item responsive-height grid-4">
                        {campaignBoxes.isLoading ? <SkeletonItem height={200} gridSpan={4} /> : <DashboardBox
                            title="Campaigns Conversions"
                            total={millify(totalCampaignConversions)}
                            data={campaignBoxes.data}
                            dataKey='orders'
                        />}
                    </div>
                    <div className="dashboard-item responsive-height grid-4">
                        {campaignBoxes.isLoading ? <SkeletonItem height={200} gridSpan={4} /> : <DashboardBox
                            title="Campaigns Revenue"
                            total={`$${millify(totalCampaignRevenue)}`}
                            data={campaignBoxes.data}
                            dataKey='revenue'
                        />}
                    </div>

                    <div className="dashboard-item grid-12 campaign-averages-container">
                        <div className="chart-container">
                            <div className="chart-title">Campaign Averages</div>
                            <CampaignAveragesChart averages={{
                                messagesDelivered: parseFloat(campaignsAvg.data?.[0]?.avg_delivered || 0) * 100,
                                read: parseFloat(campaignsAvg.data?.[0]?.avg_read || 0) * 100,
                                clicks: parseFloat(campaignsAvg.data?.[0]?.avg_clicks || 0) * 100,
                                conversions: parseFloat(campaignsAvg.data?.[0]?.avg_conversions || 0) * 100,
                            }} />
                        </div>
                    </div>



                </> : null}
            </div>
        </Page>
    );
}

export default Dashboard;